import { getEntityLabel, type EntityCopyKey } from './entities';

export const emptyStateMessages = {
  'entity.noneFound': ({ entity }: { entity: EntityCopyKey }) =>
    `No ${getEntityLabel(entity, { plural: true })} found.`,
  'entity.noneYetForProject': ({ entity }: { entity: EntityCopyKey }) =>
    `No ${getEntityLabel(entity, { plural: true })} yet for this project`,
  'entity.noneForOwner': ({ entity, owner }: { entity: EntityCopyKey; owner: EntityCopyKey }) =>
    `No ${getEntityLabel(entity, { plural: true })} for this ${getEntityLabel(owner)}`,
  'entity.noneForSelectedRange': ({ entity }: { entity: EntityCopyKey }) =>
    `No ${getEntityLabel(entity, { plural: true })} found for selected date range.`,
  'entity.noneLinkedToOwner': ({
    entity,
    owner,
  }: {
    entity: EntityCopyKey;
    owner: EntityCopyKey;
  }) => `No ${getEntityLabel(entity, { plural: true })} linked to this ${getEntityLabel(owner)}`,
  'data.noResultsFound': 'No results found.',
  'data.noDataFound': 'No data found.',
  'filters.noResults': 'No results match the current filters',
  'admin.noAllowedSignups': 'No allowed signups',
  'admin.noContactEnquiries': 'No contact enquiries',
  'admin.noFeedbackSubmissions': 'No feedback submissions',
  'admin.noRecentErrorEvents': 'No recent error events',
  'coverage.noHistoryFound': 'No coverage history found.',
  'coverage.noUploadsFound': 'No coverage uploads found.',
  'metrics.noneForRange': 'No metrics available for this range',
} as const;

export type EmptyStateMessageCatalog = typeof emptyStateMessages;
export type EmptyStateMessageKey = keyof EmptyStateMessageCatalog;

export type EmptyStateMessageArgs<K extends EmptyStateMessageKey> =
  EmptyStateMessageCatalog[K] extends (args: infer TArgs) => string ? TArgs : undefined;

export const getEmptyStateMessage = <K extends EmptyStateMessageKey>(
  key: K,
  args?: EmptyStateMessageArgs<K>,
): string => {
  const entry = emptyStateMessages[key];
  if (typeof entry === 'function') {
    return (entry as (messageArgs: EmptyStateMessageArgs<K>) => string)(
      args ?? ({} as EmptyStateMessageArgs<K>),
    );
  }
  return entry;
};

type EmptyStateSampleArgs = {
  [K in EmptyStateMessageKey]?: EmptyStateMessageArgs<K>;
};

const sampleArgs: EmptyStateSampleArgs = {
  'entity.noneFound': { entity: 'project' },
  'entity.noneYetForProject': { entity: 'executionRun' },
  'entity.noneForOwner': { entity: 'suiteRun', owner: 'suite' },
  'entity.noneForSelectedRange': { entity: 'suiteRun' },
  'entity.noneLinkedToOwner': { entity: 'test', owner: 'release' },
};

const getEmptyStateMessageSample = <K extends EmptyStateMessageKey>(
  key: K,
  args: EmptyStateMessageArgs<K> | undefined,
): { key: EmptyStateMessageKey; value: string } => ({
  key,
  value: getEmptyStateMessage(key, args),
});

export const getEmptyStateMessageSamples = (): Array<{
  key: EmptyStateMessageKey;
  value: string;
}> =>
  (Object.keys(emptyStateMessages) as EmptyStateMessageKey[]).map((key) => ({
    key,
    value: getEmptyStateMessageSample(key, sampleArgs[key] as never).value,
  }));
